import { Smarty } from '@egw/clients'
import { Logger } from '@egw/logger'
import SmartyStreetsSDK from 'smartystreets-javascript-sdk'

const Lookup = SmartyStreetsSDK.usStreet.Lookup

const log = new Logger('Smarty Streets')

// -- Getters ------------------------------------
export const getSmartyRecommendation = async (pageData: any) => {
  const streetApiClient = Smarty.getStreetApiClient()

  const {
    addressLine1 = '',
    addressLine2 = '',
    addressCity = '',
    addressRegionCode = '',
    addressPostalCode = '',
  } = pageData

  const addressLookup = new Lookup()
  addressLookup.street = addressLine1
  addressLookup.street2 = addressLine2
  addressLookup.city = addressCity
  addressLookup.state = addressRegionCode
  addressLookup.zipCode = addressPostalCode
  addressLookup.maxCandidates = 1

  const client = streetApiClient

  try {
    const res = await client.send(addressLookup)

    // returns the first candidate
    if (res.lookups[0].result.length > 0) {
      return res.lookups[0].result[0]
    }
    return false
  } catch (err) {
    log.error(err)
  }
}

// converts smarty streets candidate output to EGM address format
// discards any values that are not on the candidate
export const createAddressLines = (address: any) => {
  const addressLine1Parts = [
    address.primaryNumber,
    address.streetPredirection,
    address.streetName,
    address.streetSuffix,
    address.streetPostdirection,
  ].filter((part) => part)

  const addressLine2Parts = [
    address.secondaryNumber,
    address.secondaryDesignator,
    address.extraSecondaryNumber,
    address.extraSecondaryDesignator,
  ].filter((part) => part)

  const addressPostalCodeParts = [address.zipCode, address.plus4Code].filter((part) => part)

  const addressLine1 = addressLine1Parts.join(' ')
  const addressLine2 = addressLine2Parts.join(' ')
  const addressCity = address.cityName
  const addressRegionCode = address.state
  const addressPostalCode = addressPostalCodeParts.join('-')

  return {
    addressLine1,
    addressLine2,
    addressCity,
    addressRegionCode,
    addressPostalCode,
  }
}

interface IisSecondaryInfoRequiredProps {
  dpvFootnotes?: string | undefined
  dpvMatchCode?: string | undefined
  dpvCmra?: string | undefined
  zipType?: string | undefined

  [key: string]: string | undefined
}

export const getAddressProblems = (dpvFootnotes: string) => {
  const problemFootnoteCodes = [
    'A1', // Address not present in USPS data
    'C1', // Secondary number IS REQUIRED for delivery
    'M1', // Primary number (e.g., house number) is missing
    'N1', // Address is missing secondary information
    'P3', // PO, RR, or HC box number is invalid.
  ]

  // Assemble the regular expression pattern from the array
  const problemFootnotePattern = new RegExp(`(${problemFootnoteCodes.join('|')})`)

  // Use the regular expression to find matches in the dpvFootnotes string
  return problemFootnotePattern.exec(dpvFootnotes)
}

export const isSecondaryInfoRequired = ({ dpvFootnotes, dpvMatchCode }: SmartyStreetsSDK.usStreet.Analysis) => {
  // Check dpv_match_code
  if (dpvMatchCode === 'D') {
    return true
  }

  return getAddressProblems(dpvFootnotes) !== null // Return true if there are matches, indicating a problem
}

export const isResidentialAddress = (
  { cmra, dpvFootnotes }: SmartyStreetsSDK.usStreet.Analysis,
  { zipType, recordType }: SmartyStreetsSDK.usStreet.Metadata,
) => {
  log.debug(cmra, dpvFootnotes, zipType)
  // Define an array of problem codes
  // source: https://www.smarty.com/docs/cloud/us-street-api#dst
  const problemFootnoteCodes = [
    'PB', // PO Box style address
  ]

  // Assemble the regular expression pattern from the array
  const problemFootnotePattern = new RegExp(`(${problemFootnoteCodes.join('|')})`)

  // Use the regular expression to find matches in the dpvFootnotes string
  // a match means the address is not residential
  const matches = problemFootnotePattern.exec(dpvFootnotes)

  const notCMRA = cmra === 'N' || cmra === '' || !cmra
  const notPO = !matches && recordType !== 'P' // P means PO Box record type
  const isStandardOrMilitary = zipType === 'Standard' || zipType === 'Military'

  return notCMRA && notPO && isStandardOrMilitary // return true when residential
}
